import type { ReactNode } from "react";
import type { WebsiteConfig } from "../types/core";
import { WBConsentProvider } from "./WBConsentProvider";
import { LegalLine } from "../components/legalLine";
import { CookieBanner } from "../components/cookieBanner";
import { LanguageSwitcher } from "../lang/LanguageSwitcher";

/**
 * Everything the runtime puts AROUND the operator's page tree: the consent
 * provider, the legal line, the language switcher and the consent banner.
 *
 * One component on purpose. While these were four siblings spelled out in
 * App.tsx, the consent tests had to rebuild that tree by hand — and deleting
 * the provider from App left every test green while every published site
 * showed dead placeholders (outside the provider nothing is ever allowed).
 * App mounts THIS, the tests mount THIS.
 *
 * Must sit inside WBThemeRuntimeProvider and WBLanguageRuntimeProvider.
 */
export function RuntimeChrome({
  websiteConfig,
  children,
}: {
  websiteConfig: WebsiteConfig;
  children?: ReactNode;
}) {
  return (
    <WBConsentProvider privacyPolicyUrl={websiteConfig.privacyPolicyUrl}>
      {children}
      {/* In the page flow, AFTER the operator's tree: present with or
          without a footer node, and untouched by its visibility rules. */}
      <LegalLine websiteConfig={websiteConfig} />
      <LanguageSwitcher />
      <CookieBanner websiteConfig={websiteConfig} />
    </WBConsentProvider>
  );
}
